import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  CircularProgress, 
  IconButton, 
  ToggleButton, 
  ToggleButtonGroup, 
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Cached,
  Adjust,
  Visibility,
  Analytics,
  ShowChart,
  GridView,
  Notes,
  Functions,
  Person,
  Group,
  Checklist,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchGroupedRosterDetailsByCrewCode } from 'state/api';
import { formatTZDate } from 'state/helpers';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import MetricCard from 'components/MetricCard';
import GenericDialog from 'components/GenericDialog';
import SearchInput from 'components/SearchInput';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';
import TopUsersCard from "./TopUsersCard";
import TopCrewsCard from "./TopCrewsCard";
import DrillDownTable from "./DrillDownTable";


function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

const CrewScheduleChanges = () => {
  useAuthCheck();
  const user = useSelector((state) => state.user); 
  const { startDate, endDate } = useDateRangeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canView = hasRight(permissionMap, 'CrewScheduleChanges', 'view', false);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);

  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
  const [view, setView] = useState('stats');
  const [drillMode, setDrillMode] = useState(null); // 'user' | 'crew'
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [logDialog, setLogDialog] = useState({
    open: false,
    title: "",
    data: [],
    loading: false,
  });

  const [crewsByUser, setCrewsByUser] = useState([]);
  const [usersByCrew, setUsersByCrew] = useState([]);  
  const [summary, setSummary] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 100,
    total: 0,
  });

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchInput, 600);

  useEffect(() => {
    setSearchQuery(debouncedSearch);
  }, [debouncedSearch]);


  useEffect(() => {
    if (!startDate || !endDate) return;

    const fetchSummary = async () => {
      try {
        setError(null);
        setLoading(true);

        const url = '/crewschedulechanges/summary';
        const payload = { startDate, endDate };
        await api.post(url, payload)
          .then((res) => setSummary(res.data))
          .catch(() => setSummary(null));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    }

    fetchSummary();
  }, [startDate, endDate]);
  
  const loadGridData = useCallback(async (page, pageSize, search = "") => {
    if (!startDate || !endDate) return;
    
    try {      
      const url = '/crewschedulechanges/logsbypage';
      const payload = { startDate, endDate, page, pageSize, search };
      const res = await api.post(url, payload); 

      setGridData(res.data.data);
      if (page === 1) {
        setPagination(res.data.pagination);
      } else {
        setPagination(p => ({
          ...p,
          page,
          pageSize,
        }));
      }
    } catch (error) {
      setError(error);
    } 
  }, [startDate, endDate]);

  useEffect(() => {
    loadGridData(1, pagination.pageSize, searchQuery);
  }, [loadGridData, pagination.pageSize, searchQuery]);
    
  useEffect(() => {
    if (drillMode !== "user" || !selectedKey) return;

    setLogDialog(d => ({ ...d, loading: true }));

    const url = '/crewschedulechanges/crewsbyuser';
    const payload = { startDate, endDate, logByUser: selectedKey };

    api.post(url, payload)
      .then(res => {
        setCrewsByUser(
          res.data.map(c => ({
            crewCode: c._id,
            crewName: c.crewName,
            rank: c.rank,
            base: c.base,
            count: c.count,
          }))
        );
      })
      .finally(() =>
        setLogDialog(d => ({ ...d, loading: false }))
      );

  }, [startDate, endDate, drillMode, selectedKey]);

  useEffect(() => {
    if (drillMode !== "crew" || !selectedKey) return;

    setLogDialog(d => ({ ...d, loading: true }));

    const url = '/crewschedulechanges/usersbycrew';
    const payload = { startDate, endDate, crewCode: selectedKey };

    api.post(url, payload)
      .then(res => {
        setUsersByCrew(
          res.data.map(u => ({
            user: u._id,
            count: u.count,
          }))
        );
      })
      .finally(() =>
        setLogDialog(d => ({ ...d, loading: false }))
      );
  }, [startDate, endDate, drillMode, selectedKey]);
  
  const handleViewCrewRosters = useCallback(async (param) => {
    try {
      if (!canView) return;

      const { crewCode, crewName } = param;
      setSelectedCrewCode(crewCode);
      setSelectedCrewName(crewName);

      const result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
      
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
    } catch (error) {
      setOpenRosterDetailsDialog(false);
    } 
  }, [canView, startDate, endDate]);

  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };

  const handleUserClick = useCallback((userName) => {
    setDrillMode("user");
    setSelectedUser(userName);
    setSelectedKey(userName);
    setLogDialog({ open: true, title: userName, loading: false });
  }, []);

  const handleCrewClick = useCallback((crew) => {
    const { crewCode, crewName } = crew;
    setDrillMode("crew");
    setSelectedKey(crewCode);
    setLogDialog({ open: true, title: `${crewCode} - ${crewName}`, loading: false });
  }, []);

  const handleRowClick = async (row) => {
    // CASE 1: user → crew
    if (drillMode === "user") {
      const res = await api.post('/crewschedulechanges/logsbyuserandcrew', {
        startDate,
        endDate,
        logByUser: selectedUser,
        crewCode: row.crewCode,
      });
      return res.data;
    }

    // CASE 2: crew → user
    if (drillMode === "crew") {
      setSelectedUser(row.user);
      const res = await api.post('/crewschedulechanges/logsbyuserandcrew', {
        startDate,
        endDate,
        logByUser: row.user,
        crewCode: selectedKey,
      });
      return res.data;
    }

    return [];
  };

  const handleCloseLogDialog = () => {
    setLogDialog({ open: false, title: "", loading: false });
    setDrillMode(null);
    setSelectedKey(null);
  };

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      ...(isMobile ? { width: 60 } : { flex: 1 }),  
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const indexInPage = params.api.getAllRowIds().indexOf(params.id);
        return (pagination.page - 1) * pagination.pageSize + indexInPage + 1;
      }
    },
    {
      field: "logForDay",
      headerName: "LogDay",
      ...(isMobile ? { width: 90 } : { flex: 1.5 }), 
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${formatTZDate(value)}`;
      },
    },  
    { 
      field: "crewCode", 
      headerName: "Code", 
      ...(isMobile ? { width: 60 } : { flex: 1 }), 
    },
    {
      field: "crewName",
      headerName: "CrewName",
      ...(isMobile ? { width: 250 } : { flex: 4 }), 
    }, 
    {
      field: "logByUser",
      headerName: "LogByUser",
      ...(isMobile ? { width: 250 } : { flex: 4 }), 
    },
    {
      field: "logOnDayTime",
      headerName: "LogOnTime",
      ...(isMobile ? { width: 120 } : { flex: 2 }), 
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${formatTZDate(value, "UTC", "HH:mm dd/MM/yyyy")}`;
      },
    },      
    {
      field: "logMessage",
      headerName: "Message",
      ...(isMobile ? { width: 250 } : { flex: 4 }), 
    },  
    {
      field: "dateDiff",
      headerName: "DateDiff",
      ...(isMobile ? { width: 80 } : { flex: 1.2 }), 
    },   
  ], [isMobile, pagination.page, pagination.pageSize]);
  
  const actionColumn = useMemo(() => {        
    const activeCount = [canView].filter(Boolean).length;
    // if (activeCount === 0) return [];
    const baseWidth = 85 + activeCount * 20;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,      
        sortable: false,
        ...(isMobile ? { width: baseWidth } : { flex: 1.2 }),   
        renderCell: (params) => (
          <Box 
            height="100%" 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            gap={1}
          >           
            <Tooltip title="View">
              <IconButton
                aria-label="View"
                sx={{
                  color: theme.palette.primary.dark,
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.green.deep,
                  }
                }}
                onClick={() => {
                  handleViewCrewRosters(params.row);
                }}
              >
                <Visibility />
              </IconButton>
            </Tooltip>     
          </Box>
        ),
      }
    ]
  }, [canView, isMobile, theme, handleViewCrewRosters]); 

  const stats = useMemo(() => {
    if (!summary) return [];

    const logsPerUser = summary.userCount
      ? Number((summary.totalLogs / summary.userCount).toFixed(0))?.toLocaleString('en-US')
      : 0;

    const logsPerCrew = summary.crewCount
      ? Number((summary.totalLogs / summary.crewCount).toFixed(0))?.toLocaleString('en-US')
      : 0;

    return [
      { 
        title: "Total", 
        subtitle: "All Logs", 
        value: summary.totalLogs?.toLocaleString('en-US'), 
        icon: <Functions/>,
        bgColor: alpha(theme.palette.primary.main, 0.6),
      },
      { 
        title: "Users", 
        subtitle: "Count", 
        value: summary.userCount?.toLocaleString('en-US'), 
        icon: <Person/>,
        bgColor: alpha(theme.palette.info.main, 0.6),
      },
      { 
        title: "Crews", 
        subtitle: "Count", 
        value: summary.crewCount?.toLocaleString('en-US'), 
        icon: <Group/>,
        bgColor: alpha(theme.palette.success.main, 0.6),
      },
      { 
        title: "Logs / User", 
        subtitle: "Average", 
        value: logsPerUser, 
        icon: <Checklist/>,
        bgColor: alpha(theme.palette.warning.main, 0.6),
      },
      { 
        title: "Logs / Crew", 
        subtitle: "Average", 
        value: logsPerCrew, 
        icon: <Adjust/>,
        bgColor: alpha(theme.palette.secondary.main, 0.6),
      },
      {
        title: "Max Logs / Crew",
        subtitle: "Highest",
        value: summary.maxCrewLogs?.toLocaleString('en-US') || 0,
        icon: <Notes/>,
        bgColor: alpha(theme.palette.error.main, 0.6),
      },
    ];
  }, [theme.palette, summary]);

  const topUsers = useMemo(() => {
    if (!summary?.topUsers) return [];
    const total = summary.totalLogs || 1;

    return summary.topUsers.map(u => ({
      label: u._id || "Unknown",
      count: u.count?.toLocaleString('en-US'),
      percent: Math.round((u.count / total) * 100),
    }));
  }, [summary]);

  const topCrews = useMemo(() => {
    if (!summary?.topCrews) return [];
    const total = summary.totalLogs || 1;

    return summary.topCrews.map(c => ({
      label: `${c._id} ${c.crewName}` || "Unknown",
      crewCode: c._id,
      crewName: c.crewName,
      count: c.count?.toLocaleString('en-US'),
      percent: Math.round((c.count / total) * 100),
    }));
  }, [summary]);
  
  const logsChartData = useMemo(() => {
    if (!summary?.byDay?.length) return [];

    return [
      {
        id: "Logs",
        data: summary.byDay.map((d) => ({
          x: d._id,
          y: d.count,
        })),
      },
    ];
  }, [summary]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Crew Logs" 
          subtitle={`${loading ? 'Loading...' : 'List of Changes'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {view === "grid" && !isMobile && (
              <SearchInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            )}

            <ToggleButtonGroup
              aria-label="ViewToggle"
              exclusive
              value={view}
              onChange={(e, value) => value && setView(value)}
            >
              {[
                { value: "stats", label: "stats", icon: <Analytics /> },
                { value: "chart", label: "chart", icon: <ShowChart /> },
                { value: "grid", label: "grid", icon: <GridView /> },
              ].map(({ value, label, icon }) => (
                <ToggleButton
                  size="small"
                  key={value}
                  aria-label={label}
                  value={value}
                >
                  {icon}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
         
            {false && (
              <Tooltip title="Refresh" arrow>
                <span>
                  <StyledButton 
                    onClick={() => {
                      loadGridData(pagination.page, pagination.pageSize, searchQuery);
                    }}
                  >
                    <Cached />
                  </StyledButton>
                </span>
              </Tooltip>  
            )}
          </Box>
        )}
      </FlexBetween>                      
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box
            ref={scrollRef} 
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {view === 'stats' && (      
              <Box 
                display="flex" 
                flexDirection="column" 
                gap={2}
              >
                <Box     
                  display="grid"
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))"         
                  gridAutoRows={100}
                  gap={2}           
                  sx={{ "& > div": { gridColumn: isDesktop 
                    ? "span 2" : isTablet ? "span 4" : isMobile 
                      ? "span 6" 
                      : "span 4" } 
                  }}
                > 
                  {stats?.map((stat, index) => (
                    <MetricCard
                      key={index}
                      title={stat.title}                 
                      subtitle={stat.subtitle}      
                      value={stat.value}      
                      icon={stat.icon}      
                      bgColor={stat.bgColor}    
                    />
                  ))}        
                </Box>

                <Box         
                  display="grid"
                  gridTemplateColumns="repeat(12, minmax(0, 1fr))"       
                  gridAutoRows={86}       
                  gap={2}           
                >               
                  <Box 
                    gridColumn={
                      isDesktop ? "span 6"
                      : isTablet ? "span 12"
                      : "span 12"
                    }
                    gridRow={ "span 4" }
                    overflow="auto"
                  >
                    <TopUsersCard 
                      data={topUsers} 
                      onUserClick={handleUserClick} 
                    />
                  </Box>

                  <Box 
                    gridColumn={
                      isDesktop ? "span 6"
                      : isTablet ? "span 12"
                      : "span 12"
                    }
                    gridRow={ "span 4" }
                    overflow="auto"
                  >
                    <TopCrewsCard 
                      data={topCrews} 
                      onCrewClick={handleCrewClick} 
                    />
                  </Box>
                </Box>
              </Box>    
            )}
            {view === 'chart' && (           
              <Box
                height={LAYOUT_HEIGHT} 
                sx={styles}    
              >                            
                <SortBarMultiLinesChart
                  data={logsChartData}
                  title="Logs by Day"
                  bottomLegend="Date"
                  leftLegend="Count"
                  digit={0}
                  barTranslateXOffSet={120} 
                  lineTranslateXOffSet={-15}
                />
              </Box> 
            )}
            {view === 'grid' && (        
              <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
              >
                <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>    
                  <DataGridComponent
                    rows={gridData}
                    columns={[...columns, ...actionColumn]}
                    columnVisibilityModel={columnVisibilityModel}
                    setColumnVisibilityModel={setColumnVisibilityModel}
                    showSearch={false}
                    props={{
                      paginationMode: "server",
                      rowCount: pagination.total,
                      paginationModel: {
                        page: pagination.page - 1,
                        pageSize: pagination.pageSize,
                      },
                      pageSizeOptions: [25, 50, 100],
                      onPaginationModelChange: (model) => {
                        loadGridData(model.page + 1, model.pageSize, searchQuery);
                      },
                      onPageSizeChange: (newPageSize) => {
                        setPagination((prev) => ({
                          ...prev,
                          pageSize: newPageSize,
                        }));
                      },
                    }}
                  />
                  </Box>
              </Box>
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}
      
      <GenericDialog
        open={logDialog.open}
        onClose={handleCloseLogDialog}
        title={logDialog.title}
        loading={logDialog.loading}
      >
        {drillMode === "user" && (
          <DrillDownTable
            data={crewsByUser}
            type="crew"
            onRowClick={handleRowClick}
          />
        )}

        {drillMode === "crew" && (
          <DrillDownTable
            data={usersByCrew}
            type="user"
            onRowClick={handleRowClick}
          />
        )}
      </GenericDialog>

      {openRosterDetailsDialog &&
        <RosterDetailsDialog 
          open={openRosterDetailsDialog}
          onClose={handleCloseRosterDetailsDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewRosters={groupedCrewRosters}
        />
      }
    </Box>
  );
};

export default CrewScheduleChanges;
